/**
 * Shared LLM plumbing for the natural-language ("sentence") filters.
 *
 * Everything in this file is pure (no network, no imports from api.ts) so it can
 * be unit tested without pulling in axios / amplify.
 */

import type { FilterFieldMeta } from "@/types/filterBuilder";

/** Model used for prose-ish chat completions (care plan drafting, phone agent). */
export const DEFAULT_CHAT_MODEL = "claude-haiku-4-5-20251001";

/**
 * Model used for natural-language -> filter JSON translation.
 * gpt-5.x is a reasoning family: the backend drops `temperature` and sends
 * `reasoning_effort` for it, so FILTER_TEMPERATURE is only honoured by
 * non-reasoning models.
 */
export const FILTER_MODEL = "gpt-5.6-terra";

/** Filter translation is a deterministic mapping task: no sampling. */
export const FILTER_TEMPERATURE = 0;

export const FILTER_MAX_TOKENS = 1024;

// ---------------------------------------------------------------------------
// JSON extraction
// ---------------------------------------------------------------------------

export class JsonExtractionError extends Error {
  raw: string;

  constructor(message: string, raw: string) {
    super(message);
    this.name = "JsonExtractionError";
    this.raw = raw;
  }
}

/**
 * Pull a single JSON object out of an LLM response.
 *
 * Tolerates: ``` / ```json fences, prose before or after the object, and
 * trailing commentary. Strategy: strip fences, then take the substring from the
 * first `{` to the last `}` and parse that; if that fails, walk the last `}`
 * backwards to find the longest parseable prefix-balanced candidate.
 *
 * Throws {@link JsonExtractionError} (carrying the raw text) when nothing
 * parseable is found, or when the parsed value is not a plain object.
 */
export function extractJsonObject(text: string): Record<string, unknown> {
  const raw = text ?? "";
  let candidate = raw.trim();

  if (!candidate) {
    throw new JsonExtractionError("Empty model response", raw);
  }

  // Strip markdown code fences (```json ... ``` or ``` ... ```).
  candidate = candidate
    .replace(/^\s*```[a-zA-Z0-9_-]*\s*/, "")
    .replace(/\s*```\s*$/, "")
    .trim();

  // If the whole response is valid JSON, honour it directly — that also lets us
  // reject a top-level array/scalar instead of digging an object out of it.
  try {
    const whole = JSON.parse(candidate);
    if (whole !== null && typeof whole === "object" && !Array.isArray(whole)) {
      return whole as Record<string, unknown>;
    }
    throw new JsonExtractionError("Model response was not a JSON object", raw);
  } catch (e) {
    if (e instanceof JsonExtractionError) throw e;
    // Not valid JSON on its own — fall through to brace extraction.
  }

  const start = candidate.indexOf("{");
  if (start === -1) {
    throw new JsonExtractionError("No JSON object found in model response", raw);
  }

  // Try progressively shorter candidates, from the last `}` backwards.
  let end = candidate.lastIndexOf("}");
  while (end > start) {
    const slice = candidate.slice(start, end + 1);
    try {
      const parsed = JSON.parse(slice);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new JsonExtractionError(
          "Model response was not a JSON object",
          raw
        );
      }
      return parsed as Record<string, unknown>;
    } catch (e) {
      if (e instanceof JsonExtractionError) throw e;
      end = candidate.lastIndexOf("}", end - 1);
    }
  }

  throw new JsonExtractionError("Could not parse JSON from model response", raw);
}

// ---------------------------------------------------------------------------
// Allowed-key derivation + sanitising
// ---------------------------------------------------------------------------

/**
 * The concrete API keys a set of filter-builder fields maps to. Composite
 * fields (range inputs) expand to their `rangeKeys` start/end pair and the
 * composite key itself is NOT allowed, since the API does not accept it.
 */
export function allowedKeysFromMeta(
  fields: FilterFieldMeta[],
  extras: string[] = []
): string[] {
  const keys = new Set<string>();
  for (const field of fields) {
    if (field.rangeKeys) {
      keys.add(field.rangeKeys.start);
      keys.add(field.rangeKeys.end);
    } else {
      keys.add(field.key);
    }
  }
  for (const extra of extras) keys.add(extra);
  return Array.from(keys);
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (typeof value === "number" && Number.isNaN(value)) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value as object).length === 0
  ) {
    return true;
  }
  return false;
}

export interface SanitizeResult<T> {
  filters: T;
  /** Keys the model produced that are not part of the allowed set. */
  droppedKeys: string[];
  /** Allowed keys whose value was null / empty and therefore removed. */
  emptyKeys: string[];
}

/**
 * Keep only keys in `allowedKeys`, and drop null/empty values. `false` and `0`
 * are meaningful filter values and are preserved.
 */
export function sanitizeFilters<T>(
  raw: Record<string, unknown>,
  allowedKeys: string[]
): SanitizeResult<T> {
  const allowed = new Set(allowedKeys);
  const filters: Record<string, unknown> = {};
  const droppedKeys: string[] = [];
  const emptyKeys: string[] = [];

  for (const [key, value] of Object.entries(raw)) {
    if (!allowed.has(key)) {
      droppedKeys.push(key);
      continue;
    }
    if (isEmptyValue(value)) {
      emptyKeys.push(key);
      continue;
    }
    filters[key] = value;
  }

  return { filters: filters as T, droppedKeys, emptyKeys };
}

/**
 * JSON schema (Anthropic tool input_schema shape) restricted to the allowed
 * keys, so the backend can force structurally valid output. Types come from the
 * filter-builder metadata; anything unknown is typed as a string.
 */
export function buildFilterJsonSchema(
  fields: FilterFieldMeta[],
  allowedKeys: string[],
  typeOverrides: Record<string, "string" | "number" | "boolean"> = {}
): Record<string, unknown> {
  const typeByKey = new Map<string, "string" | "number" | "boolean">();
  for (const field of fields) {
    if (field.rangeKeys) {
      const t = field.inputType === "number-range" ? "number" : "string";
      typeByKey.set(field.rangeKeys.start, t);
      typeByKey.set(field.rangeKeys.end, t);
    } else if (field.inputType === "boolean") {
      typeByKey.set(field.key, "boolean");
    } else if (field.inputType === "number") {
      typeByKey.set(field.key, "number");
    } else {
      typeByKey.set(field.key, "string");
    }
  }
  for (const [key, type] of Object.entries(typeOverrides)) {
    typeByKey.set(key, type);
  }

  const properties: Record<string, unknown> = {};
  for (const key of allowedKeys) {
    properties[key] = { type: typeByKey.get(key) ?? "string" };
  }

  return {
    type: "object",
    properties,
    additionalProperties: false,
  };
}

// ---------------------------------------------------------------------------
// Date anchors
// ---------------------------------------------------------------------------

export interface DateAnchors {
  today: string;
  yesterday: string;
  tomorrow: string;
  days7Ago: string;
  days30Ago: string;
  days90Ago: string;
  weekStart: string;
  weekEnd: string;
  monthStart: string;
  lastMonthStart: string;
  lastMonthEnd: string;
}

function toLocalIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Pre-computed date anchors in the user's local timezone, so the model never
 * has to do date arithmetic. Weeks start on Monday.
 */
export function buildDateAnchors(now: Date = new Date()): DateAnchors {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // getDay(): 0 = Sunday. Monday-based offset:
  const mondayOffset = (today.getDay() + 6) % 7;
  const weekStart = addDays(today, -mondayOffset);
  const weekEnd = addDays(weekStart, 6);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  return {
    today: toLocalIsoDate(today),
    yesterday: toLocalIsoDate(addDays(today, -1)),
    tomorrow: toLocalIsoDate(addDays(today, 1)),
    days7Ago: toLocalIsoDate(addDays(today, -7)),
    days30Ago: toLocalIsoDate(addDays(today, -30)),
    days90Ago: toLocalIsoDate(addDays(today, -90)),
    weekStart: toLocalIsoDate(weekStart),
    weekEnd: toLocalIsoDate(weekEnd),
    monthStart: toLocalIsoDate(monthStart),
    lastMonthStart: toLocalIsoDate(lastMonthStart),
    lastMonthEnd: toLocalIsoDate(lastMonthEnd),
  };
}

/** Prompt block listing the anchors as literal values the model must copy. */
export function renderDateAnchors(anchors: DateAnchors): string {
  return [
    "## Date anchors (already computed — copy these literal values, never compute dates yourself)",
    "",
    `- today: ${anchors.today}`,
    `- yesterday: ${anchors.yesterday}`,
    `- tomorrow: ${anchors.tomorrow}`,
    `- 7 days ago: ${anchors.days7Ago}`,
    `- 30 days ago: ${anchors.days30Ago}`,
    `- 90 days ago: ${anchors.days90Ago}`,
    `- start of this week (Monday): ${anchors.weekStart}`,
    `- end of this week (Sunday): ${anchors.weekEnd}`,
    `- start of this month: ${anchors.monthStart}`,
    `- start of last month: ${anchors.lastMonthStart}`,
    `- end of last month: ${anchors.lastMonthEnd}`,
  ].join("\n");
}

/** Shared output contract appended to every filter system prompt. */
export function renderOutputRules(allowedKeys: string[]): string {
  return [
    "## Output rules",
    "",
    "- Respond with exactly ONE JSON object and nothing else: no markdown, no code fences, no prose, no explanation.",
    `- Use ONLY these keys: ${allowedKeys.join(", ")}.`,
    "- Omit every key the user did not ask for. Do not fill in defaults.",
    "- Prefer the most specific filter that expresses the request; do not add extra filters that merely sound related.",
    "- Never invent tag names, statuses or enum values that are not listed above.",
    "- All start/end date filters are inclusive, and dates must come from the date anchors.",
    "- If the request is ambiguous, or cannot be expressed with these keys, return exactly {}.",
  ].join("\n");
}
